import { useCallback, useMemo } from 'react';
import type { ButtonProperties } from 'ui';
import {
  useGetDialInHrefEvent,
  useHandleRsvpStatus,
  useOnRequestEnterEvent,
  useRefreshingJoinPeriod,
  useRsvpStatusDropdownOptions,
} from '../../hooks';
import type { EventActionsView, EventData } from '../../types';
import { isAfterJoinPeriod, isJoinPeriodNode } from '../../utils';
import { EventActionsButtons } from '../event-actions-buttons';

type P = {
  event: EventData;
  hasLabel?: boolean;
  type?: ButtonProperties['type'];
  view?: EventActionsView;
};

const EventActions = ({ event, hasLabel, type, view = 'list' }: P) => {
  const { handleRsvpStatus, isAccepting, isRejecting } = useHandleRsvpStatus({ data: event });

  const { isJoining, onRequestEnterEvent } = useOnRequestEnterEvent();

  const isPastEvent = isAfterJoinPeriod(event.endTime);
  const { dropdownOptions } = useRsvpStatusDropdownOptions({
    authorId: event.author,
    endTime: event.endTime,
    onGoing() {
      handleRsvpStatus('going');
    },
    onNotGoing() {
      handleRsvpStatus('not_going');
    },
    rsvp: event.rsvp,
  });

  const isJoinPeriodRefreshing = useRefreshingJoinPeriod(useCallback(() => isJoinPeriodNode(event), [event]));

  const dialInData = useGetDialInHrefEvent({ dialInInformation: event.dialInInformation, role: event.role });

  const dropdownButtonProperties = useMemo(
    () => ({
      dropdownOptions,
      isAccepting,
      isRejecting,
      type,
    }),
    [dropdownOptions, isAccepting, isRejecting, type],
  );

  const joinButtonProperties = useMemo(
    () => ({
      dialInData,
      isJoining,
      onEnter() {
        onRequestEnterEvent({
          id: event.id,
          type: 'required-auth',
        });
      },
    }),
    [isJoining, onRequestEnterEvent, event, dialInData],
  );

  const rsvpButtonsProperties = useMemo(
    () => ({
      actionGoing() {
        handleRsvpStatus('going');
      },
      actionNotGoing() {
        handleRsvpStatus('not_going');
      },
      hasLabel,
      isAccepting,
      isRejecting,
    }),
    [handleRsvpStatus, hasLabel, isAccepting, isRejecting],
  );

  return (
    <EventActionsButtons
      currentUserRsvp={event.rsvp}
      dropdownButton={dropdownButtonProperties}
      isAfterJoinPeriod={isPastEvent}
      isJoinPeriod={isJoinPeriodRefreshing}
      joinButton={joinButtonProperties}
      rsvpButtons={rsvpButtonsProperties}
      view={view}
    />
  );
};

export default EventActions;
